import React from "react";
import { useAppContext } from "../../AppProvider.jsx";
import InputIcon from "../../Components/Inputs/InputIcon/InputIcon.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFieldError } from "../../utilities/utilities.ts";
import {
  faCalendarCheck,
  faLoader,
  faWeightHanging,
} from "../../utilities/fontawesomeIcons.js";
import { Collection } from "../../Models/Collection.ts";
import ButtonConfirm from "../../Components/Buttons/Confirm.tsx";
import AsyncSelect from "../../Components/Selects/AsyncSelect/AsyncSelect.tsx";
import ButtonUtility from "../../Components/Buttons/Utility.tsx";
import { useCollectionHandlerContext } from "./providers/hooks.ts";
import { CarrierConfiguration } from "../../Models/CarrierConfiguration.ts";
import {
  getAllCarriers,
  getAllCarriersFromCarrierConfigurations,
} from "../../Components/Selects/AsyncSelect/api.ts";
import { CarrierConfigurationOption } from "../../Components/Selects/AsyncSelect/styles.tsx";
import Datepicker from "../../Components/DatePickers/DatePicker/DatePicker.tsx";
import TimePicker from "../../Components/TimePickers/TimePicker.tsx";
import { AddressBookHandlerProvider } from "../AddressBook/providers/AddressBookHandlerProvider.tsx";
import { AddressBookHandler } from "../AddressBook/AddressBookHandler.tsx";
import { defaultPaginatedResponse } from "../../Models/Pagination.ts";
import moment from "moment";
import { Alert } from "reactstrap";
import { Shipment } from "../../Models/Shipment.ts";
import { Link } from "react-router-dom";

/* ===========================================================================
 * COMPONENT: CollectionHandler
 * =========================================================================== */

/**
 * @component CollectionHandler
 * * The primary form component for creating, editing, and viewing Collection details.
 * It integrates:
 * - Carrier selection with standalone creation logic.
 * - Cutoff time validation and alerts.
 * - Pickup address management via AddressBookHandlerProvider.
 * - Time slot configuration for morning and afternoon.
 * - Listing of related shipments (in details mode).
 *
 * @returns {JSX.Element} The rendered collection form or loader.
 */
export const CollectionHandler = () => {
  const { dictionary, accessTokenPayload } = useAppContext();

  /** * Destructuring values from the CollectionHandlerContext.
   * This context manages the state and persistence logic for the current collection.
   */
  const {
    id,
    data,
    error,
    loader,
    title,
    action,
    autosave,
    standAlone,
    defaultAddressBookId,
    relatedShipments,
    callback,
    saveCollection,
    saveCollectionAndBook,
  } = useCollectionHandlerContext();

  /* ---------------------------------------------------------------------------
   * HELPER FUNCTIONS
   * ------------------------------------------------------------------------- */

  /**
   * Universal change handler for form inputs.
   * Updates the collection state and triggers autosave if enabled.
   */
  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string,
  ) => {
    if (action !== "details") {
      saveCollection(
        {
          ...data,
          [name]: value,
        } as Collection,
        key as keyof Collection,
        autosave,
      );
    }
  };

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <React.Fragment>
      <div className="row">
        <div className="col">
          {loader ? (
            /* Main Loader State */
            <div className="row">
              <div className="col text-center">
                <FontAwesomeIcon icon={faLoader} spin size="2x" />
              </div>
            </div>
          ) : (
            <React.Fragment>
              {/* Cutoff Time Warning Alert: Displayed if current time exceeds carrier cutoff */}
              {data.carrierConfiguration?.carrier?.collectionCutoffTime &&
                moment(data.date).isSame(moment(), "day") &&
                moment().isAfter(
                  moment(
                    data.carrierConfiguration?.carrier?.collectionCutoffTime,
                    "HH:mm",
                  ),
                ) && (
                  <div className="row mb-3">
                    <div className="col">
                      <Alert color="warning">
                        {dictionary.collections.cut_off_passed} (
                        {
                          data.carrierConfiguration?.carrier
                            ?.collectionCutoffTime
                        }
                        )
                      </Alert>
                    </div>
                  </div>
                )}

              <h3 className="text-center">{title}</h3>

              {/* Carrier Selection Section: Only visible if editing an existing collection or in standalone mode */}
              {((id && data.carrierConfiguration) || (!id && standAlone)) && (
                <div className="row gx-2 mb-1">
                  <div className="col-12">
                    <label className="label-2">
                      {dictionary.registries.carrier}
                    </label>
                    <AsyncSelect
                      name="carrierConfiguration"
                      optionValue="id"
                      optionLabel="name"
                      value={data.carrierConfiguration ?? null}
                      onSearch={(name, value) => {
                        handleChange(name, value, "carrierConfiguration");
                      }}
                      loadOptions={async (inputValue, pagination) => {
                        // 1. Fetch carriers that allow standalone collection creation
                        const enabledCodes = (
                          await getAllCarriers({
                            inputValue: "",
                            pagination,
                            sorting: { selector: "name", orderBy: "asc" },
                            search: [
                              {
                                selector: "collectionStandaloneCreationEnabled",
                                value: true,
                              },
                            ],
                          })
                        ).map((x) => x.code);

                        if (!enabledCodes.length)
                          return { ...defaultPaginatedResponse };

                        // 2. Map enabled carriers to available user configurations
                        return getAllCarriersFromCarrierConfigurations({
                          inputValue,
                          pagination,
                          sorting: { selector: "name", orderBy: "asc" },
                          search: [
                            {
                              selector: "carrierCode",
                              value: enabledCodes,
                              content: true,
                            },
                          ],
                        });
                      }}
                      optionStyle={(option: CarrierConfiguration) => (
                        <CarrierConfigurationOption option={option} />
                      )}
                      isClearable={false}
                      error={getFieldError(error, "carrierConfiguration")}
                      placeholder={dictionary.actions.select}
                      isDisabled={action === "details"}
                    />
                  </div>
                </div>
              )}

              {/* General Info: Date, Reference, Packages and Weight */}
              <div className="row gx-2">
                <div className="col-6 col-md-3 mb-1">
                  <label className="label-2">
                    {dictionary.dates.collection_date}
                  </label>
                  <Datepicker
                    name="date"
                    selectedDate={data?.date || null}
                    onChange={(name, value) =>
                      handleChange(name, value, "date")
                    }
                    minDate={moment().toDate()}
                    error={getFieldError(error, "date")}
                    isDisabled={action === "details"}
                  />
                </div>
                <div className="col-6 col-md-3 mb-1">
                  <label className="label-2">
                    {dictionary.references.external_reference}
                  </label>
                  <InputIcon
                    name="externalReference"
                    value={data.externalReference}
                    placeholder={"EXTREF123"}
                    type="text"
                    onChange={({ target }) =>
                      handleChange(
                        target.name,
                        target.value,
                        "externalReference",
                      )
                    }
                    disabled={action === "details"}
                    error={getFieldError(error, "externalReference")}
                  />
                </div>
                {((id && data.carrierConfiguration?.id) ||
                  (!id && standAlone)) && (
                  <>
                    <div className="col-6 col-md-3 mb-1">
                      <label className="label-2">
                        {dictionary.parcels.number_of_packages_2}:
                      </label>
                      <InputIcon
                        name="numPackages"
                        value={data?.numPackages}
                        type="positive-integer-number"
                        onChange={({ target }) =>
                          handleChange(target.name, target.value, "numPackages")
                        }
                        disabled={action === "details"}
                        placeholder="1"
                        error={getFieldError(error, "numPackages")}
                      />
                    </div>
                    <div className="col-6 col-md-3 mb-1">
                      <label className="label-2">
                        {dictionary.dimensions.total_weight} (kg)
                      </label>
                      <InputIcon
                        name="totalWeight"
                        value={data?.totalWeight}
                        type="positive-number"
                        onChange={({ target }) =>
                          handleChange(target.name, target.value, "totalWeight")
                        }
                        icon={faWeightHanging}
                        disabled={action === "details"}
                        placeholder="1.00"
                        error={getFieldError(error, "totalWeight")}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Pickup Address Section: Managed via nested AddressBook provider */}
              <AddressBookHandlerProvider
                id={data.addressBookId ?? defaultAddressBookId}
                autosave={true}
                label={`${dictionary.registries.pickup}`}
                ownerId={accessTokenPayload.owner}
                callback={(addressBook) => {
                  if (addressBook) {
                    handleChange(
                      "addressBookId",
                      addressBook.id,
                      "addressBookId",
                    );
                  }
                }}
                service="shipment"
                action={
                  !data.addressBookId && defaultAddressBookId ? "clone" : action
                }
                searchInAddressBookParams={[
                  {
                    selector: "ownerId",
                    value: accessTokenPayload.owner,
                    precise: true,
                  },
                ]}
                registry={{ saveEnabled: false, searchEnabled: true }}
                inError={!!getFieldError(error, "addressBookId")}
                getErrors={(errors) => {
                  if (errors)
                    handleChange("addressBookId", null, "addressBookId");
                }}
              >
                <AddressBookHandler />
              </AddressBookHandlerProvider>

              {/* Time Slots Section */}
              <h3 className="m-0 mb-2">{dictionary.times.time_slots}</h3>
              <div className="row gx-2 mb-1">
                <div className="col-6 col-md-3 mb-1">
                  <label className="label-2">
                    {dictionary.collections.opening_morning}
                  </label>
                  <TimePicker
                    name="morningOpenTime"
                    selectedTime={data?.morningOpenTime || null}
                    onChange={(name, value) =>
                      handleChange(name, value, "morningOpenTime")
                    }
                    minHour={8}
                    maxHour={13}
                    error={getFieldError(error, "morningOpenTime")}
                    isDisabled={action === "details"}
                  />
                </div>
                <div className="col-6 col-md-3 mb-1">
                  <label className="label-2">
                    {dictionary.collections.closing_morning}
                  </label>
                  <TimePicker
                    name="morningCloseTime"
                    selectedTime={data?.morningCloseTime || null}
                    onChange={(name, value) =>
                      handleChange(name, value, "morningCloseTime")
                    }
                    minHour={8}
                    maxHour={14}
                    error={getFieldError(error, "morningCloseTime")}
                    isDisabled={action === "details"}
                  />
                </div>
                <div className="col-6 col-md-3 mb-1">
                  <label className="label-2">
                    {dictionary.collections.opening_afternoon}
                  </label>
                  <TimePicker
                    name="afternoonOpenTime"
                    selectedTime={data?.afternoonOpenTime || null}
                    onChange={(name, value) =>
                      handleChange(name, value, "afternoonOpenTime")
                    }
                    minHour={14}
                    maxHour={17}
                    error={getFieldError(error, "afternoonOpenTime")}
                    isDisabled={action === "details"}
                  />
                </div>
                <div className="col-6 col-md-3 mb-1">
                  <label className="label-2">
                    {dictionary.collections.closing_afternoon}
                  </label>
                  <TimePicker
                    name="afternoonCloseTime"
                    selectedTime={data?.afternoonCloseTime || null}
                    onChange={(name, value) =>
                      handleChange(name, value, "afternoonCloseTime")
                    }
                    minHour={14}
                    maxHour={18}
                    error={getFieldError(error, "afternoonCloseTime")}
                    isDisabled={action === "details"}
                  />
                </div>
              </div>

              {/* Related Shipments Section: Links to shipments currently in this collection */}
              {action === "details" && relatedShipments.length > 0 && (
                <>
                  <h3 className="m-0 mb-2">
                    {dictionary.collections.shipments_in_collection}
                  </h3>
                  <div className="row gx-2">
                    {relatedShipments.map((s: Shipment) => (
                      <div className="col-4 mb-1" key={s.id}>
                        <Link
                          target="_blank"
                          to={`/shipments/details/${s.id}`}
                          className="label-5 text-decoration-underline d-inline-block"
                        >
                          {s.internalReference}
                        </Link>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Form Action Buttons */}
              <div className="d-flex mt-3">
                <ButtonUtility
                  className="w-50 me-2"
                  onClick={() => callback?.()}
                  disabled={loader}
                >
                  {dictionary.actions.cancel}
                </ButtonUtility>

                {action !== "details" && (
                  <>
                    {/* Save Button (Standalone only) */}
                    {standAlone && (
                      <ButtonConfirm
                        className="w-50 me-2"
                        onClick={() =>
                          saveCollection(data as Collection, null, true)
                        }
                        disabled={loader}
                        loading={loader}
                      >
                        {dictionary.actions.save} {dictionary.words.in}{" "}
                        {dictionary.status.draft}
                      </ButtonConfirm>
                    )}
                    {/* Save & Book Combined Action */}
                    <ButtonConfirm
                      className="w-50"
                      onClick={() =>
                        saveCollectionAndBook(data as Collection, null, true)
                      }
                      disabled={loader}
                      loading={loader}
                      icon={false}
                    >
                      <FontAwesomeIcon icon={faCalendarCheck} />
                      {dictionary.actions.save} {dictionary.words.and}{" "}
                      {dictionary.actions.book.toLowerCase()}
                    </ButtonConfirm>
                  </>
                )}
              </div>
            </React.Fragment>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
